import React from 'react';

/**
 * Interface for the HeartIcon component props.
 * @interface HeartIconProps
 * @property {string} [className] - Optional CSS class name for styling the SVG
 * @property {string} [color] - Optional color for the heart icon (default: 'currentColor')
 * @property {string} [size] - Optional size for the icon (default: '1.5rem')
 * @property {boolean} [filled] - Optional flag to render a filled heart (default: false)
 */
interface HeartIconProps {
  className?: string;
  color?: string;
  size?: string;
  filled?: boolean;
}

/**
 * A customizable heart icon component using Feather icon design.
 * Used for favorites/wishlist indicators, like buttons, or other affection-related UI elements.
 * 
 * @component
 * @example
 * // Heart icon with default color and size
 * <HeartIcon />
 * 
 * @example
 * // Filled heart icon with custom color, size and class
 * <HeartIcon 
 *   color="#FF0000" 
 *   size="1.5rem"
 *   className="favorite-icon"
 *   filled={true}
 * />
 * 
 * @param {HeartIconProps} props - Component props
 * @param {string} [props.className=''] - CSS class name for additional styling
 * @param {string} [props.color='currentColor'] - Stroke and fill (when filled) color of the icon
 * @param {string} [props.size='1.5rem'] - Size of the icon
 * @param {boolean} [props.filled=false] - Whether the heart should be filled or outlined
 * @returns {JSX.Element} SVG heart icon
 */
const HeartIcon: React.FC<HeartIconProps> = ({
  className = '',
  color = 'currentColor',
  size = '1.5rem',
  filled = false
}) => {
  return (
    <svg
      className={`feather feather-heart ${className}`}
      fill={filled ? color : 'none'}
      height={size}
      width={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.25"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        display: 'inline-block',
        verticalAlign: 'middle'
      }}
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
};

export default HeartIcon;
